// src/pages/BTLMortgageCalculator.jsx
// SEO-optimised BTL mortgage calculator & sourcing landing page
// Target keywords: buy to let mortgage calculator UK, BTL mortgage calculator,
// buy to let mortgage rates UK, best buy to let mortgage, landlord mortgage calculator,
// BTL mortgage comparison, buy to let mortgage sourcing, HMO mortgage calculator,
// portfolio landlord mortgage UK, limited company buy to let mortgage

import React from "react";
import TopNav from "../../components/TopNav.jsx";
import SiteFooter from "../../components/SiteFooter.jsx";
import {
  Calculator,
  Search,
  Check,
  ArrowRight,
  SlidersHorizontal,
  FileText,
  BarChart3,
  Clock3,
  BadgeCheck,
  Building2,
  TrendingUp,
  Shield,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";

const HERO_SRC = "/BTL-calculator.webp";

export default function BTLMortgageCalculator() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = HERO_SRC;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  React.useEffect(() => {
    document.title =
      "Buy to Let Mortgage Calculator UK 2026 | Compare BTL Rates & Source Online | Dynesourcer";

    const metaDescription = document.querySelector('meta[name="description"]');
    const content =
      "Free buy to let mortgage calculator for UK landlords. Compare BTL mortgage rates, monthly repayments, arrangement fees and true cost. Filter by LTV, property type, borrower profile and more. Source, shortlist and apply online — broker team reviews every application.";

    if (metaDescription) {
      metaDescription.setAttribute("content", content);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = content;
      document.head.appendChild(meta);
    }
  }, []);

  return (
    <main className="min-h-screen bg-white text-slate-800">
      <TopNav />

      {/* ── Hero ── */}
      <section
        className="relative h-[560px] w-full overflow-hidden"
        aria-label="Buy to let mortgage calculator UK — compare BTL rates and source online"
      >
        <img
          src={HERO_SRC}
          alt="Buy to let mortgage calculator UK — compare BTL mortgage rates for landlords and property investors"
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-sky-950/60" />
        <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
          <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            Free BTL mortgage calculator
          </div>
          <h1 className="text-4xl font-black tracking-tight md:text-6xl leading-tight max-w-5xl">
            Buy to Let Mortgage Calculator UK — Compare Rates &amp; Source Online
          </h1>
          <p className="mt-5 max-w-3xl text-base text-white/90 md:text-lg leading-relaxed">
            Enter your property value, deposit, mortgage amount and estimated rent to instantly
            compare buy-to-let mortgage products matched to your eligibility. See live rates,
            monthly repayments, arrangement fees and true cost over the product term —
            then shortlist and apply online in minutes.
          </p>
          <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-7 py-3.5 text-lg font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-sky-600 hover:shadow-lg"
            >
              <Calculator className="h-5 w-5" /> Use BTL calculator now
            </Link>
            <Link
              to="/explore-borrower-journey"
              className="inline-flex items-center gap-2 rounded-lg bg-white/15 backdrop-blur-sm border border-white/30 px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-white/25"
            >
              See how it works
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-white/90">
            <span className="rounded-full bg-white/15 px-3 py-1">Free to use</span>
            <span className="rounded-full bg-white/15 px-3 py-1">Whole-of-market BTL products</span>
            <span className="rounded-full bg-white/15 px-3 py-1">17 advanced filters</span>
            <span className="rounded-full bg-white/15 px-3 py-1">Broker-reviewed applications</span>
            <span className="rounded-full bg-white/15 px-3 py-1">HMO & limited company supported</span>
          </div>
        </div>
      </section>

      {/* ── Trust bar ── */}
      <div style={{ backgroundColor: "rgb(2 132 199)" }} className="text-white py-3">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm font-semibold">
            {[
              "Free BTL mortgage calculator",
              "Compare live UK rates",
              "HMO & portfolio landlord criteria",
              "Limited company (SPV) mortgages",
              "Free to source & enquire",
            ].map((item) => (
              <span key={item} className="flex items-center gap-2">
                <Check className="h-4 w-4 flex-shrink-0" /> {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── What the calculator does ── */}
      <section
        className="bg-white py-16 md:py-20"
        aria-label="What the Dynesourcer buy to let mortgage calculator does"
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
              More than a buy to let mortgage calculator — a full BTL sourcing platform
            </h2>
            <p className="mt-4 text-slate-600 md:text-lg leading-relaxed">
              Most BTL mortgage calculators give you a rough repayment figure and nothing else.
              Dynesourcer goes further — connecting your property and borrower details directly
              to live UK buy-to-let mortgage products matched to your actual eligibility.
              Calculate, compare, shortlist and apply, all in one place.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                <Calculator className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Instant BTL mortgage calculations</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Enter property value, deposit, mortgage amount and estimated rent. Instantly
                see matched products with interest rate, monthly repayment, arrangement fee
                and total cost over the product term — updated in real time as you adjust figures.
              </p>
            </div>

            <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                <SlidersHorizontal className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">17 advanced BTL filters</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Filter results using real lender criteria — LTV, EPC rating, property type,
                borrower type, BTL experience, tenancy type, taxpayer rate, tenure, deposit
                source and more. Only see products that genuinely match your profile.
              </p>
            </div>

            <div className="rounded-xl border border-sky-100 bg-sky-50 p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-600">
                <BadgeCheck className="h-6 w-6" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Shortlist, apply &amp; track</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Shortlist up to 5 products, submit an online application and receive an
                instant mortgage illustration. Our expert broker team review every case
                before submission to your chosen lender — then you track progress live.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Who it's for ── */}
      <section
        className="bg-slate-50 py-16 md:py-20"
        aria-label="Who the buy to let mortgage calculator is for"
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
              BTL mortgage calculator for every type of landlord
            </h2>
            <p className="mt-3 text-lg text-slate-600">
              Whether you're buying your first rental property or refinancing a portfolio,
              our calculator and sourcing platform supports the full range of buy-to-let
              borrower profiles.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <Building2 className="h-5 w-5" />,
                title: "First-time landlords",
                desc: "No buy-to-let experience required with many lenders. Calculate affordability and see products available to first-time investors before committing.",
                bullets: ["No BTL experience required", "25% deposit minimum typically", "Residential ownership not always required"],
              },
              {
                icon: <TrendingUp className="h-5 w-5" />,
                title: "Portfolio landlords",
                desc: "Own four or more mortgaged properties? Filter specifically for portfolio landlord criteria and whole-portfolio ICR lenders.",
                bullets: ["Background portfolio accepted", "Whole portfolio ICR assessment", "Up to 75% LTV on individual properties"],
              },
              {
                icon: <Search className="h-5 w-5" />,
                title: "HMO & MUFB investors",
                desc: "Filter for HMO and multi-unit freehold block mortgage products with specialist criteria and higher rental yield calculations.",
                bullets: ["Licensed HMO mortgages", "MUFB specialist criteria", "Higher rental yield ICR calculations"],
              },
              {
                icon: <FileText className="h-5 w-5" />,
                title: "Limited company (SPV) landlords",
                desc: "Source buy-to-let mortgages for Special Purpose Vehicles and trading limited companies — filter by taxpayer type (DSC) for accurate results.",
                bullets: ["SPV & trading Ltd company criteria", "Basic & higher taxpayer DSC filters", "Corporation tax-efficient structures"],
              },
              {
                icon: <BarChart3 className="h-5 w-5" />,
                title: "Remortgage & product transfer",
                desc: "Coming off a fixed rate? Compare remortgage products across the whole market and calculate the true saving versus your lender's revert rate.",
                bullets: ["Whole-market remortgage comparison", "Capital raising options", "Day 1 remortgage filter available"],
              },
              {
                icon: <Shield className="h-5 w-5" />,
                title: "Specialist & complex cases",
                desc: "EPC filter, ex-local authority, leasehold remaining term, deposit origination — advanced filters for cases that need specialist lender criteria.",
                bullets: ["EPC rating filter", "Ex-local authority property filter", "Leasehold remaining term criteria"],
              },
            ].map((card) => (
              <article
                key={card.title}
                className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600 mb-3">
                  {card.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{card.title}</h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">{card.desc}</p>
                <ul className="space-y-2">
                  {card.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" /> {b}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section
        className="bg-white py-16 md:py-20"
        aria-label="How the Dynesourcer BTL mortgage calculator and sourcing process works"
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid gap-10 md:grid-cols-2 md:items-start">
            <div>
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                How to use the buy to let mortgage calculator
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our BTL mortgage calculator is the entry point to a full sourcing and
                application journey — from affordability check to broker-submitted
                application, all in one platform.
              </p>

              <div className="mt-8 space-y-6">
                {[
                  {
                    icon: <Calculator className="h-5 w-5" />,
                    title: "1. Enter your property and loan details",
                    desc: "Add property value, deposit amount, mortgage amount and estimated monthly rent. The calculator auto-adjusts deposit and LTV figures as you type.",
                  },
                  {
                    icon: <SlidersHorizontal className="h-5 w-5" />,
                    title: "2. Apply BTL filters to narrow results",
                    desc: "Use advanced filters — LTV, EPC, borrower type, BTL experience, tenancy type, taxpayer rate, tenure and more — to see only products that match your specific scenario.",
                  },
                  {
                    icon: <BarChart3 className="h-5 w-5" />,
                    title: "3. Compare live matched products",
                    desc: "See buy-to-let mortgage products in real time with interest rate, monthly repayment, arrangement fee and total cost over the product term — sortable and comparable side by side.",
                  },
                  {
                    icon: <FileText className="h-5 w-5" />,
                    title: "4. Shortlist and submit online enquiry",
                    desc: "Shortlist up to 5 products, register or sign in, and submit an online application. Free to enquire — you receive an instant mortgage illustration in return.",
                  },
                  {
                    icon: <BadgeCheck className="h-5 w-5" />,
                    title: "5. Broker fact-finds and submits to lender",
                    desc: "Our specialist broker team contact you to fact-find, validate the application and submit it to the lender on your behalf. A broker fee applies at this stage.",
                  },
                  {
                    icon: <Clock3 className="h-5 w-5" />,
                    title: "6. Track your application live",
                    desc: "Monitor progress from submission through to offer and completion via your Dynesourcer dashboard — full visibility at every stage.",
                  },
                ].map((step) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600 shadow-sm">
                      {step.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{step.title}</h3>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Filters grid */}
            <div className="rounded-2xl border border-sky-100 bg-white p-7 shadow-sm">
              <h3 className="text-2xl font-black text-slate-900 mb-2">
                17 BTL filters built around real lender criteria
              </h3>
              <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                Every filter maps to a real lender criterion — so the products you see
                are ones you can actually get, not just the cheapest headline rates.
              </p>

              <div className="grid gap-2.5 sm:grid-cols-2">
                {[
                  "LTV",
                  "EPC rating",
                  "Mortgage term",
                  "Product term",
                  "Product type",
                  "Loan purpose",
                  "Borrower type",
                  "Home owner status",
                  "BTL experience",
                  "Credit worthiness",
                  "Tenancy type",
                  "Taxpayer type (DSC)",
                  "Freehold or leasehold",
                  "Leasehold remaining term",
                  "Ex-local authority",
                  "Deposit origination",
                  "Day 1 remortgage",
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-lg border border-sky-100 bg-sky-50 px-4 py-2.5 text-sm font-medium text-slate-700 flex items-center gap-2"
                  >
                    <Check className="h-3.5 w-3.5 text-sky-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl bg-sky-600 p-5 text-white">
                <p className="text-sm font-semibold mb-3">What borrowers see instantly</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "Interest rate",
                    "Monthly repayment",
                    "Arrangement fee",
                    "Cost over product term",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 flex-shrink-0 text-sky-300" /> {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why Dynesourcer ── */}
      <section
        className="bg-sky-50 py-16 md:py-20"
        aria-label="Why use Dynesourcer buy to let mortgage calculator instead of other tools"
      >
        <div className="mx-auto max-w-6xl px-4">
          <div className="grid gap-10 md:grid-cols-2 items-center">
            <div>
              <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
                Why Dynesourcer beats a standard BTL mortgage calculator
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                A standard buy-to-let mortgage calculator shows you an estimated monthly
                repayment based on a rate you type in. That's useful for rough planning —
                but it tells you nothing about which products you'd actually qualify for,
                what the arrangement fees are, or how the true cost compares across lenders.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Dynesourcer connects the calculation directly to live lender products.
                Every figure you see — rate, repayment, fee, total cost — is from a real
                buy-to-let mortgage product available in the UK market today, matched to
                your borrower profile and property criteria.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  "Live products, not estimated figures based on a rate you typed in",
                  "Eligibility-matched — only products you can actually get",
                  "True cost comparison including arrangement fees over the product term",
                  "Shortlist, apply and track — all in the same journey",
                  "Expert broker reviews every application before lender submission",
                  "Free to calculate, source and enquire — broker fee only on full application",
                ].map((point) => (
                  <div key={point} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />
                    {point}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-sky-900 p-8 text-white shadow-lg">
              <h3 className="text-2xl font-black mb-6">
                Standard BTL calculator vs Dynesourcer
              </h3>
              <div className="space-y-4">
                {[
                  { label: "Shows live lender products", standard: false, ds: true },
                  { label: "Eligibility-matched results", standard: false, ds: true },
                  { label: "Arrangement fee included", standard: false, ds: true },
                  { label: "True cost over product term", standard: false, ds: true },
                  { label: "Advanced borrower filters", standard: false, ds: true },
                  { label: "HMO & limited company criteria", standard: false, ds: true },
                  { label: "Shortlist & apply online", standard: false, ds: true },
                  { label: "Broker reviews application", standard: false, ds: true },
                  { label: "Monthly repayment estimate", standard: true, ds: true },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-4 text-sm border-b border-white/10 pb-3 last:border-0 last:pb-0">
                    <span className="text-white/90">{row.label}</span>
                    <div className="flex gap-6 text-center flex-shrink-0">
                      <span className={`w-20 text-xs font-semibold rounded-full px-2 py-0.5 ${row.standard ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                        {row.standard ? "✓ Yes" : "✕ No"}
                      </span>
                      <span className="w-20 text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-500/20 text-emerald-300">
                        ✓ Yes
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end gap-4 text-xs text-white/50">
                <span>Standard</span>
                <span>Dynesourcer</span>
              </div>
              <Link
                to="/sourcing/buy-to-let-mortgage-sourcing"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-sky-500 px-6 py-3 font-semibold text-white transition-all hover:bg-sky-400"
              >
                Use the calculator now <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Social proof ── */}
      <section
        className="bg-white py-14"
        aria-label="What landlords say about the Dynesourcer BTL mortgage calculator"
      >
        <div className="mx-auto max-w-5xl px-4">
          <h2 className="text-2xl font-black text-slate-900 text-center mb-10">
            What landlords say about Dynesourcer
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                text: "Found an HMO mortgage I couldn't source anywhere else. The filters matched me to a lender who accepted my property type — saved weeks of phone calls.",
                author: "Michael R.",
                role: "HMO Investor, London",
              },
              {
                text: "The true cost comparison was the game-changer. The cheapest rate wasn't the cheapest product once I factored in arrangement fees. Dynesourcer showed that instantly.",
                author: "Sarah M.",
                role: "Portfolio Landlord",
              },
              {
                text: "As a first-time landlord I had no idea where to start. The filters made it easy — I just told the platform my situation and it showed me exactly what I could get.",
                author: "Lisa K.",
                role: "First-time Landlord",
              },
            ].map((t, i) => (
              <blockquote key={i} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-4">"{t.text}"</p>
                <footer className="border-t border-slate-100 pt-3">
                  <p className="font-bold text-slate-900 text-sm">{t.author}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        className="bg-sky-50 py-16 md:py-20"
        aria-label="Frequently asked questions about the buy to let mortgage calculator"
      >
        <div className="mx-auto max-w-4xl px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-slate-900 md:text-4xl">
              Buy to let mortgage calculator — FAQs
            </h2>
            <p className="mt-3 text-slate-600">
              Common questions from UK landlords and property investors about our
              BTL calculator and mortgage sourcing platform.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "How does the buy to let mortgage calculator work?",
                a: "Enter your property value, deposit, mortgage amount and estimated monthly rent. The calculator instantly matches you to live UK buy-to-let mortgage products showing interest rate, monthly repayment, arrangement fee and total cost over the product term. If you change the deposit, the mortgage amount and LTV update automatically.",
              },
              {
                q: "Is the buy to let mortgage calculator free to use?",
                a: "Yes. The BTL mortgage calculator and the full sourcing platform are free to use. There's no charge to compare products, apply filters or submit an online enquiry. A broker fee applies if you choose to proceed to a full mortgage application — you'll be told the fee before you commit.",
              },
              {
                q: "What buy to let mortgage filters are available?",
                a: "There are 17 filters mapped to real lender criteria: LTV, EPC rating, mortgage term, product term, product type, loan purpose, borrower type, home owner status, BTL experience, credit worthiness, tenancy type, taxpayer type (DSC), freehold or leasehold, leasehold remaining term, ex-local authority, deposit origination and day 1 remortgage.",
              },
              {
                q: "Can I use the calculator for HMO and limited company BTL mortgages?",
                a: "Yes. The platform supports HMO (Houses in Multiple Occupation), multi-unit freehold blocks (MUFBs), limited company (SPV) mortgages and portfolio landlords. Use the borrower type and property filters to show only products that match your specific structure.",
              },
              {
                q: "What happens after I shortlist products and apply?",
                a: "You can shortlist up to 5 products and submit a free online enquiry. Our specialist broker team will contact you to fact-find and confirm the best-fit product. Once you're happy to proceed, the broker prepares and submits the full application to the lender. You then track progress live through your dashboard.",
              },
              {
                q: "How accurate are the buy to let mortgage rates shown?",
                a: "The rates displayed are sourced directly from lenders who have listed their products on Dynesourcer. They reflect live product data including interest rate, LTV, arrangement fee and eligibility criteria — not estimated or indicative figures.",
              },
              {
                q: "Can I use the BTL calculator to compare remortgage products?",
                a: "Yes. Select 'remortgage' as your loan purpose to filter specifically for buy-to-let remortgage products. You can also use the Day 1 remortgage filter for properties purchased recently, and compare true cost to assess whether switching lender makes financial sense.",
              },
            ].map((item) => (
              <details
                key={item.q}
                className="group rounded-xl border border-sky-100 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900 list-none">
                  <span>{item.q}</span>
                  <span className="ml-4 text-sky-500 transition-transform group-open:rotate-180 flex-shrink-0">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-16 text-center text-white"
        style={{ backgroundColor: "rgb(2 132 199)" }}
        aria-label="Use the buy to let mortgage calculator and source BTL products now"
      >
        <div className="mx-auto max-w-4xl px-4">
          <h2 className="text-3xl font-black md:text-4xl">
            Ready to compare buy to let mortgage rates?
          </h2>
          <p className="mt-3 text-white/90 max-w-2xl mx-auto leading-relaxed">
            Use our free BTL mortgage calculator — enter your property details, compare
            live rates filtered by your eligibility, shortlist and apply online.
            Free to source and enquire. Expert broker team handles the rest.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/sourcing/buy-to-let-mortgage-sourcing"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 font-bold text-sky-700 transition-all hover:-translate-y-0.5 hover:bg-slate-100 hover:shadow-lg"
            >
              <Calculator className="h-5 w-5" /> Use BTL calculator now
            </Link>
            <Link
              to="/explore-borrower-journey"
              className="inline-flex items-center gap-2 rounded-lg bg-white/15 border border-white/30 px-7 py-3.5 font-semibold text-white transition-all hover:bg-white/25"
            >
              See the borrower journey
            </Link>
          </div>
          <p className="mt-4 text-xs text-white/70">
            Free to source &amp; enquire &nbsp;•&nbsp; Broker fee applies on full application &nbsp;•&nbsp; No obligation
          </p>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}